/**
 * Jobs API service
 * Handles all job-related API requests
 */

import { get, post, patch, del, buildQueryString } from './api';
import type {
  Job,
  JobListItem,
  JobsResponse,
  CreateJobInput,
  UpdateJobInput,
  JobFilter,
  JobProgress,
} from '@/types/job';
import type { GridCellsResponse, GridCell } from '@/types/grid';

/**
 * API endpoints
 */
const ENDPOINTS = {
  JOBS: '/jobs',
  JOB: (id: string) => `/jobs/${id}`,
  JOB_PROGRESS: (id: string) => `/jobs/${id}/progress`,
  JOB_CELLS: (id: string) => `/jobs/${id}/cells`,
  JOB_CELL_LOGS: (id: string, cellId: string) => `/jobs/${id}/cells/${cellId}/logs`,
  JOB_RETRY: (id: string) => `/jobs/${id}/retry`,
  JOB_RETRY_CELLS: (id: string) => `/jobs/${id}/retry-cells`,
  JOB_RESULTS: (id: string) => `/jobs/${id}/results`,
  JOB_EXPORT: (id: string) => `/jobs/${id}/export`,
};

/**
 * Backend response format for jobs list
 */
interface JobsBackendResponse {
  data: JobListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Format coordinates as a readable string
 */
function formatCoordinates(bounds: { north: number; south: number; east: number; west: number }): string {
  const centerLat = (bounds.north + bounds.south) / 2;
  const centerLng = (bounds.east + bounds.west) / 2;
  return `${centerLat.toFixed(4)}°, ${centerLng.toFixed(4)}°`;
}

/**
 * Transform job data to include placesFound alias and location description
 */
function transformJob<T extends { totalPlaces?: number; bounds?: { north: number; south: number; east: number; west: number }; locationName?: string }>(job: T): T & { placesFound: number; locationDisplay: string } {
  const bounds = job.bounds;
  let locationDisplay = job.locationName || '';

  if (!locationDisplay && bounds) {
    locationDisplay = formatCoordinates(bounds);
  }

  return {
    ...job,
    placesFound: job.totalPlaces || 0,
    locationDisplay,
  };
}

/**
 * Get paginated list of jobs
 */
export async function getJobs(filter?: JobFilter): Promise<JobsResponse> {
  // Map frontend filter names to backend expected names
  const backendFilter: Record<string, unknown> = {};
  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined) {
        // Map 'search' to 'searchQuery' for backend compatibility
        if (key === 'search') {
          backendFilter['searchQuery'] = value;
        } else {
          backendFilter[key] = value;
        }
      }
    });
  }
  const queryString = Object.keys(backendFilter).length > 0 ? buildQueryString(backendFilter) : '';
  const response = await get<JobsBackendResponse>(`${ENDPOINTS.JOBS}${queryString}`);

  // Transform backend response to JobsResponse format
  const { data, pagination } = response;
  return {
    jobs: (data || []).map(transformJob),
    total: pagination?.total || 0,
    page: pagination?.offset ? Math.floor(pagination.offset / (pagination.limit || 20)) : 0,
    limit: pagination?.limit || 20,
    totalPages: pagination?.total ? Math.ceil(pagination.total / (pagination.limit || 20)) : 0,
  };
}

/**
 * Get a single job by ID
 */
export async function getJob(jobId: string): Promise<Job> {
  const job = await get<Job>(ENDPOINTS.JOB(jobId));
  return transformJob(job);
}

/**
 * Create a new scraping job
 */
export async function createJob(input: CreateJobInput): Promise<Job> {
  return post<Job, CreateJobInput>(ENDPOINTS.JOBS, input);
}

/**
 * Update a job (pause/resume/cancel)
 */
export async function updateJob(jobId: string, input: UpdateJobInput): Promise<Job> {
  return patch<Job, UpdateJobInput>(ENDPOINTS.JOB(jobId), input);
}

/**
 * Delete a job
 */
export async function deleteJob(jobId: string): Promise<void> {
  return del<void>(ENDPOINTS.JOB(jobId));
}

/**
 * Pause a running job
 */
export async function pauseJob(jobId: string): Promise<Job> {
  return updateJob(jobId, { status: 'paused' });
}

/**
 * Resume a paused job
 */
export async function resumeJob(jobId: string): Promise<Job> {
  return updateJob(jobId, { status: 'running' });
}

/**
 * Cancel a job
 */
export async function cancelJob(jobId: string): Promise<Job> {
  return updateJob(jobId, { status: 'cancelled' });
}

/**
 * Get job progress (detailed status)
 */
export async function getJobProgress(jobId: string): Promise<JobProgress> {
  return get<JobProgress>(ENDPOINTS.JOB_PROGRESS(jobId));
}

/**
 * Get all grid cells for a job
 */
export async function getJobCells(jobId: string): Promise<GridCellsResponse> {
  return get<GridCellsResponse>(ENDPOINTS.JOB_CELLS(jobId));
}

/**
 * Get a specific cell
 */
export async function getJobCell(jobId: string, cellId: string): Promise<GridCell> {
  return get<GridCell>(`${ENDPOINTS.JOB_CELLS(jobId)}/${cellId}`);
}

/**
 * Retry a failed job
 */
export async function retryJob(jobId: string): Promise<Job> {
  return post<Job>(ENDPOINTS.JOB_RETRY(jobId));
}

/**
 * Retry specific failed cells
 */
export async function retryCells(jobId: string, cellIds: string[]): Promise<void> {
  return post<void, { cellIds: string[] }>(ENDPOINTS.JOB_RETRY_CELLS(jobId), { cellIds });
}

/**
 * Retry all failed cells
 */
export async function retryAllFailedCells(jobId: string): Promise<void> {
  return post<void>(ENDPOINTS.JOB_RETRY_CELLS(jobId));
}

/**
 * Skip a failed cell
 */
export async function skipCell(jobId: string, cellId: string): Promise<void> {
  return patch<void>(`${ENDPOINTS.JOB_CELLS(jobId)}/${cellId}`, { status: 'skipped' });
}

/**
 * Get job export URL
 */
export function getExportUrl(jobId: string, format: 'csv' | 'xlsx' | 'json'): string {
  return `${ENDPOINTS.JOB_EXPORT(jobId)}?format=${format}`;
}

/**
 * Delete multiple jobs
 */
export async function deleteJobs(jobIds: string[]): Promise<void> {
  await Promise.all(jobIds.map((id) => deleteJob(id)));
}

/**
 * Get recent jobs (for dashboard)
 */
export async function getRecentJobs(limit = 5): Promise<JobListItem[]> {
  const response = await getJobs({
    limit,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  return response.jobs;
}

/**
 * Get running jobs count
 */
export async function getRunningJobsCount(): Promise<number> {
  const response = await getJobs({
    status: 'running',
    limit: 1,
  });
  return response.total;
}

/**
 * Estimate grid cells for given bounds
 */
export async function estimateGridCells(
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  },
  options?: {
    zoomLevel?: number;
    overlapPercent?: number;
  }
): Promise<{
  totalCells: number;
  estimatedTime: { min: number; max: number };
  estimatedPlaces: { min: number; max: number };
}> {
  return post('/jobs/estimate', { bounds, ...options });
}

/**
 * Crawl step log entry
 */
export interface CrawlStep {
  timestamp: string;
  step: string;
  status: 'started' | 'completed' | 'failed' | 'progress';
  duration?: number;
  details?: Record<string, unknown>;
}

/**
 * Cell logs response
 */
export interface CellLogsResponse {
  jobId: string;
  cellId: string;
  steps: CrawlStep[];
}

/**
 * Get crawl step logs for a specific cell
 */
export async function getCellLogs(jobId: string, cellId: string): Promise<CellLogsResponse> {
  return get<CellLogsResponse>(ENDPOINTS.JOB_CELL_LOGS(jobId, cellId));
}
